import {useMobileBreakpoint} from '@customink/pigment-react/lib/utils/useBreakpoint';
import {useRollbar} from '@rollbar/react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import fireUnbundledPriceTestEvent from 'utils/unbundledPricingAnalytics';
import {T_SHIRT_SUBCAT_IDS} from '../utils/constants/categoryConstants';
import {unbundledPriceTest} from '../utils/constants/signalmanConstants';
import {ProductCardPlaceholders} from './Placeholders';
import ProductCardRenderer from './ProductCardRenderer';
import useAlgoliaRulesProductBadges from './useAlgoliaRulesProductBadges';
import useProductAnalyticsEvents from './useProductAnalyticsEvents';

function CustomHits({
  algoliaIndex,
  backColor,
  badges,
  categoryId,
  categoryTree,
  cid,
  compareHandler,
  compareStyles,
  currentFilters,
  defaultQuoteQty,
  firstImage,
  frontColor,
  hits,
  iframer,
  indexName,
  internalCatalog,
  quantity,
  quoteQuantityDirty,
  renderSkeletons,
  sendEvent,
  siteWideSearch,
  ydhSettings
}) {
  const rollbar = useRollbar();
  const [defaultQuoteQtyMap, setDefaultQuoteQtyMap] = useState({});
  const fireAnalytics = useRef(false);

  const isMobile = useMobileBreakpoint();
  const unbundledPriceTestEligible = !internalCatalog && !iframer;

  const showUnbundledPriceCategoryLevel =
    unbundledPriceTestEligible &&
    T_SHIRT_SUBCAT_IDS.includes(Number(categoryId)) &&
    unbundledPriceTest === 'test';

  const showUnbundledPriceSiteWideSearch =
    unbundledPriceTestEligible &&
    siteWideSearch &&
    unbundledPriceTest === 'test';

  const productBadges = useAlgoliaRulesProductBadges({
    badges,
    categoryId,
    enabled: true,
    hits
  });

  const {fireSelectProductEvent} = useProductAnalyticsEvents({
    categoryId,
    categoryTree,
    fireProductImpressionsOnLoad: !renderSkeletons,
    hits,
    isSearchResults: siteWideSearch
  });

  useEffect(() => {
    dataLayer.push({event: 'Hits Viewed'});
  }, [hits]);

  useEffect(() => {
    if (siteWideSearch && algoliaIndex) {
      let isMounted = true;
      const primaryCategoryDefaultQty = {};

      algoliaIndex
        .browseRules({
          batch: (rules) => {
            rules.forEach((rule) => {
              const result = rule.objectID.toString().match(/^qr_(\d+)$/);
              const catId = result?.[1];
              if (catId && !primaryCategoryDefaultQty[catId]) {
                const defaultQty = parseInt(
                  rule?.consequence?.userData?.defaultQuoteQty,
                  10
                );
                if (defaultQty > 0)
                  primaryCategoryDefaultQty[catId] = defaultQty;
              }
            });
          }
        })
        .then(() => {
          if (isMounted) setDefaultQuoteQtyMap(primaryCategoryDefaultQty);
        })
        .catch((error) => {
          rollbar.error('Error browsing Algolia rules', error);
        });
      return () => {
        isMounted = false;
      };
    }
    return undefined;
  }, [algoliaIndex?.indexName, siteWideSearch]);

  useEffect(() => {
    if (!fireAnalytics.current) {
      if (
        unbundledPriceTestEligible &&
        (T_SHIRT_SUBCAT_IDS.includes(Number(categoryId)) || siteWideSearch)
      ) {
        fireUnbundledPriceTestEvent();
      }
      fireAnalytics.current = true;
    }
  }, []);

  const currentSelectedColorFilters = useMemo(() => {
    const refinementAttributes = [
      'colors.simple_names',
      'min_qty_colors.simple_names'
    ];
    const colorRefinement = currentFilters.find((refinementItem) =>
      refinementAttributes.includes(refinementItem.attribute)
    );

    return (
      colorRefinement?.refinements?.map((colorFilter) => colorFilter.value) ||
      []
    );
  }, [currentFilters]);

  const minQtyToggleSet = useMemo(() => {
    return currentFilters.some(
      (currentFilter) =>
        currentFilter.attribute === 'has_singles_enabled_colors'
    );
  }, [currentFilters]);

  return (
    <div className="pc-Products" data-insights-index={indexName}>
      {/* Skeleton */}
      {renderSkeletons && (
        <ProductCardPlaceholders isMobile={isMobile} firstImage={firstImage} />
      )}
      {/* Correct hits from Algolia */}
      {!renderSkeletons &&
        hits?.map((hit, index) => (
          <ProductCardRenderer
            key={hit.product_id}
            backColor={backColor}
            categoryId={categoryId}
            cid={cid}
            compareHandler={compareHandler}
            compareStyles={compareStyles}
            currentSelectedColorFilters={currentSelectedColorFilters}
            defaultQuoteQty={defaultQuoteQty}
            defaultQuoteQtyMap={defaultQuoteQtyMap}
            fireSelectProductEvent={fireSelectProductEvent}
            frontColor={frontColor}
            hit={hit}
            iframer={iframer}
            index={index}
            internalCatalog={internalCatalog}
            minQtyToggleSet={minQtyToggleSet}
            productBadges={productBadges}
            quantity={quantity}
            quoteQuantityDirty={quoteQuantityDirty}
            siteWideSearch={siteWideSearch}
            ydhSettings={ydhSettings}
            showUnbundledPriceCategoryLevel={showUnbundledPriceCategoryLevel}
            showUnbundledPriceSiteWideSearch={showUnbundledPriceSiteWideSearch}
            sendEvent={sendEvent}
          />
        ))}
    </div>
  );
}

export default CustomHits;
